import { Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import appConfig from '../../../defs/appConfig';
import { DirectionId } from '../../defs/block';
import { config } from '../../defs/config';
import { GameScene } from '../../GameScene';
import { getCollisionTracker } from '../../util/CollisionTracker';
import { mapFromViewPosition } from '../../util/mapTools';
import { IEffect } from './IEffect';
import {
  SodaBlockEntity,
  despawnBlockEntity,
  unplaceBlockEntity,
} from '../../entities/BlockEntity';

// types
//-----------------------------------------------------------------------------
export type SodaEffectOptions = {
  entity: SodaBlockEntity;
};

type AnimateProps = {
  getToPosition: (position: Vector2) => Vector2;
  iterateVector: Vector2;
};

// constants
//-----------------------------------------------------------------------------
const animationMap: Record<DirectionId, AnimateProps> = {
  // left
  left: {
    getToPosition: (position: Vector2) =>
      new Vector2(-(config.tile.size + appConfig.size.width), position.y),
    iterateVector: new Vector2(-1, 0),
  },
  // right
  right: {
    getToPosition: (position: Vector2) =>
      new Vector2(config.tile.size + appConfig.size.width, position.y),
    iterateVector: new Vector2(1, 0),
  },
  // up
  up: {
    getToPosition: (position: Vector2) =>
      new Vector2(position.x, -(config.tile.size + appConfig.size.height)),
    iterateVector: new Vector2(0, -1),
  },
  // down
  down: {
    getToPosition: (position: Vector2) =>
      new Vector2(position.x, config.tile.size + appConfig.size.height),
    iterateVector: new Vector2(0, 1),
  },
};

/*
    soda launch effect
*/
export class SodaLaunchEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: SodaBlockEntity;
  // state
  private readonly _collisionTracker = getCollisionTracker();

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: SodaEffectOptions) {
    this._scene = scene;
    this._entity = options.entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const direction = this._entity.c.blockSoda.direction;
    await this._launchSoda(direction);
  }

  // private
  //-------------------------------------------------------------------------
  private async _launchSoda(direction: DirectionId) {
    const animationProps = animationMap[direction];
    const { x, y } = this._entity.view;
    const position = { x, y };
    const from = new Vector2(position.x, position.y);
    const mapLast = mapFromViewPosition(from);
    const map = this._scene.sessionEntity.c.map;

    // determine "to" position
    const to = animationProps.getToPosition(from);

    // solve duration from travel distance of linear ease
    const duration = from.sub(to).length() / config.sim.soda.speed;
    const buildUpDelay = 0.2;

    // play launch VFX
    this._entity.c.blockSoda.view.playLaunchFx();
    this._scene.view2d.setEntityLayer(this._entity, 'front');

    // Remove block from map, leaving the position empty for new blocks.
    // The enitity still exists and will count towards the goal when removed at the end of the tween.
    unplaceBlockEntity(this._scene, this._entity);

    // animate and despawn soda
    return new Promise<void>((resolve) => {
      // animate
      gsap
        .to(position, {
          x: to.x,
          y: to.y,
          duration,
          ease: 'linear',
          delay: buildUpDelay,
          onUpdate: () => {
            const current = new Vector2(position.x, position.y);
            const mapCurrent = mapFromViewPosition(current);

            // iterate from last to current. this is done to handle lost frames.
            while (!mapLast.equals(mapCurrent)) {
              // iterate
              mapLast.add(animationProps.iterateVector);

              // collide at this position
              this._collisionTracker.collideAt(
                this._scene.sessionEntity.c.map,
                mapLast,
                'attack',
                {
                  type: 'soda',
                },
              );
            }

            // Resolve soda when it goes out of map range
            if (
              mapLast.x < 0 ||
              mapLast.x > map.columns ||
              mapLast.y < 0 ||
              mapLast.y > map.rows
            ) {
              resolve();
            }
          },
        })
        .then(() => {
          // then despawn soda
          despawnBlockEntity(this._scene, this._entity);
        });
    });
  }
}
